import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PlanFeatureItem } from 'webapp/enums/enums';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type { BillingPlan } from 'webapp/models-custom';
import type { PlanFeatureItemValue } from 'webapp/models-custom/billing-plan';

interface Args {
	plans: BillingPlan[];
}

interface FeatureRow {
	name: string;
	values: PlanFeatureItemValue[];
	info?: string;
	isSoon?: boolean;
}

interface FeatureSection {
	name: string;
	rows: FeatureRow[];
}

// Declares which comparison items appear in each section and in what order.
// Names, info tooltips and per-plan values are resolved from the billing
// service and the plan model — this component only owns layout and rendering.
export const SECTIONS: { name: string; items: PlanFeatureItem[] }[] = [
	{
		name: 'Data & integrations',
		items: [
			PlanFeatureItem.DATA_SOURCES,
			PlanFeatureItem.GOVERNED_SEMANTIC_LAYER,
			PlanFeatureItem.DATA_LINEAGE,
			PlanFeatureItem.CLOUD_INTEGRATIONS,
			PlanFeatureItem.CUSTOM_INTEGRATIONS,
			PlanFeatureItem.DATASETS,
			PlanFeatureItem.DATASET_TRANSFORMATIONS,
			PlanFeatureItem.DATABOX_API,
			PlanFeatureItem.VERIFY_BADGE_ASSETS,
			PlanFeatureItem.HISTORICAL_DATA,
			PlanFeatureItem.MAX_SYNC_FREQUENCY,
		],
	},
	{
		name: 'AI',
		items: [
			PlanFeatureItem.AI_CREDITS,
			PlanFeatureItem.AI_ANALYST,
			PlanFeatureItem.PERFORMANCE_SUMMARIES,
			PlanFeatureItem.MCP_SERVER,
			PlanFeatureItem.ARTIFACTS,
			PlanFeatureItem.PREBUILT_SKILLS,
			PlanFeatureItem.CUSTOM_SKILLS,
			PlanFeatureItem.GENIE_IN_SLACK,
			PlanFeatureItem.ROUTINES,
			PlanFeatureItem.AGENTS,
			PlanFeatureItem.MCP_CONNECTORS,
		],
	},
	{
		name: 'Reporting & collaboration',
		items: [
			PlanFeatureItem.SUB_ACCOUNTS,
			PlanFeatureItem.USERS,
			PlanFeatureItem.DASHBOARDS_REPORTS,
			PlanFeatureItem.CUSTOM_METRICS,
			PlanFeatureItem.PREBUILT_METRICS_TEMPLATES,
			PlanFeatureItem.ACCOUNT_TEMPLATES,
			PlanFeatureItem.LOOPED_DATABOARDS,
			PlanFeatureItem.GOALS,
			PlanFeatureItem.ANOMALY_DETECTION,
			PlanFeatureItem.SHARING,
			PlanFeatureItem.CSV_EXPORT,
			PlanFeatureItem.MEDIA_CREATIVES,
			PlanFeatureItem.CUSTOM_DATE_RANGES,
			PlanFeatureItem.UNBRANDED_DASHBOARDS,
			PlanFeatureItem.REPORT_SLIDE_LIBRARY,
			PlanFeatureItem.DATA_STORIES,
			PlanFeatureItem.SMART_ALERTS,
			PlanFeatureItem.OKRS,
			PlanFeatureItem.FISCAL_CALENDAR,
			PlanFeatureItem.WHITE_LABELING,
			PlanFeatureItem.SSO,
		],
	},
	{
		name: 'Support & services',
		items: [
			PlanFeatureItem.CHAT_EMAIL_SUPPORT,
			PlanFeatureItem.SECURITY_COMPLIANCE_REVIEW,
			PlanFeatureItem.DATA_SEMANTIC_LAYER_SETUP,
			PlanFeatureItem.ACCOUNT_SETUP,
			PlanFeatureItem.AI_IMPLEMENTATION,
			PlanFeatureItem.ENABLEMENT,
		],
	},
];

export default class BillingPlanFeaturesTableComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;

	get maxColspan() {
		return this.args.plans.length + 1;
	}

	get sectionsData(): FeatureSection[] {
		const plans = this.args.plans;

		const is2026SeptBillingGroup = this.billingService.is2026SeptBillingGroup;
		const isAgency = this.accountService.isAgency;

		return SECTIONS.map((section) => {
			const rows: FeatureRow[] = [];

			section.items.forEach((item) => {
				const { name, description, hideOn2026SeptView, isSoon } =
					this.billingService.getPlanFeatureItemInfo(item);

				if (hideOn2026SeptView && is2026SeptBillingGroup) {
					return;
				}

				if (!isAgency && item === PlanFeatureItem.WHITE_LABELING) {
					return;
				}

				if (!isAgency && item === PlanFeatureItem.SUB_ACCOUNTS) {
					return;
				}

				rows.push({
					name,
					info: description,
					values: plans.map((p) => p.getPlanFeatureItemValue(item)),
					isSoon: !!isSoon,
				});
			});

			return {
				name: section.name,
				rows,
			};
		});
	}

	<template>
		<div class="row plans-all-features">
			<h3>Full plan comparison</h3>
			<table width="100%" cellspacing="0" cellpadding="0">
				<colgroup>
					<col width="40%" />
					{{#each @plans}}
						<col />
					{{/each}}
				</colgroup>
				<thead>
					<tr>
						<th></th>
						{{#each @plans as |plan|}}
							<th class="plan-{{plan.planType}}">
								<span>{{plan.name}}</span>
							</th>
						{{/each}}
					</tr>
				</thead>
				<tbody>
					{{#each this.sectionsData as |section|}}
						<tr>
							<td class="section_title" colspan={{this.maxColspan}}>
								{{section.name}}
							</td>
						</tr>
						{{#each section.rows as |row|}}
							<tr>
								<td class="name">
									{{row.name}}
									{{#if row.isSoon}}
										(Soon)
									{{/if}}
									{{#if row.info}}
										<ToolTipWithIconComponent
											@text={{row.info}}
											@iconName="Question"
											@maxWidth={{230}}
										/>
									{{/if}}
								</td>
								{{#each row.values as |value|}}
									<td>
										{{#if value.isAddon}}
											<span class="addon">Add-on</span>
										{{else if (eq value.flag false)}}
											<span class="none">—</span>
										{{else}}
											<span class="text">
												{{#if (eq value.flag true)}}
													<CommonUiIconComponent
														@name="Check"
														@size="md"
													/>
												{{/if}}
												{{value.text}}
											</span>
										{{/if}}
										{{#if value.tooltip}}
											<ToolTipWithIconComponent
												@text={{value.tooltip}}
												@iconName="Question"
												@maxWidth={{230}}
											/>
										{{/if}}
									</td>
								{{/each}}
							</tr>
						{{/each}}
					{{/each}}
				</tbody>
			</table>
		</div>
	</template>
}
